/**
 * Deterministic lead scoring for the super CRM.
 *
 * The score is computed on demand from a Person's current signals — it
 * is never stored, so it can't drift out of sync with the record. The
 * function returns the 0–100 number, a coarse `band` (cold/warm/hot)
 * for color-coding, and a transparent breakdown of the factors so the
 * UI can explain *why* a contact scored the way it did.
 *
 * Signals, by category:
 *   - Prospect: pipeline stage, estimated deal value, touch recency,
 *     follow-up overdue.
 *   - Customer: lifecycle status, account value, touch recency,
 *     follow-up overdue.
 *
 * "Touch recency" uses the most recent of (last activity entry,
 * lastTouch). Dead-end states (closed-lost / churned) are pinned cold
 * regardless of the other signals.
 */

import type { Person } from '../db/types';

export type LeadBand = 'cold' | 'warm' | 'hot';

export interface ScoreFactor {
  label: string;
  /** Signed contribution to the score. */
  points: number;
}

export interface LeadScore {
  /** Clamped 0–100. */
  score: number;
  band: LeadBand;
  factors: ScoreFactor[];
}

const DAY_MS = 86_400_000;

/** ISO timestamp of the most recent signal we have for a person. */
export function lastTouchAt(person: Person): string | undefined {
  const acts = person.activity ?? [];
  let latest = acts.length ? acts[acts.length - 1].at : undefined;
  // Activity is append-only but be defensive about ordering.
  for (const a of acts) {
    if (!latest || Date.parse(a.at) > Date.parse(latest)) latest = a.at;
  }
  const lt = person.lastTouch;
  if (lt && (!latest || Date.parse(lt) > Date.parse(latest))) latest = lt;
  return latest;
}

function daysSince(iso?: string): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, (Date.now() - t) / DAY_MS);
}

/** Bucketed value points so a $120k deal outscores a $5k one. */
function valuePoints(value?: number): number {
  if (!value) return 0;
  if (value >= 100_000) return 20;
  if (value >= 50_000) return 15;
  if (value >= 20_000) return 10;
  if (value >= 5_000) return 5;
  return 2;
}

/** Recency points from the freshest touch. Stale relationships decay. */
function recencyFactor(person: Person): ScoreFactor | undefined {
  const days = daysSince(lastTouchAt(person));
  if (days === undefined) {
    return { label: 'No touch logged', points: -8 };
  }
  if (days <= 7) return { label: 'Touched this week', points: 15 };
  if (days <= 30) return { label: 'Touched this month', points: 6 };
  if (days <= 60) return { label: 'Quiet ~1 month', points: -5 };
  return { label: `Stale ${Math.round(days)}d`, points: -15 };
}

/** Is the follow-up date in the past (relative to local today)? */
export function isFollowUpOverdue(person: Person): boolean {
  if (!person.followUpDate) return false;
  const due = Date.parse(person.followUpDate);
  if (Number.isNaN(due)) return false;
  // Compare on date granularity — overdue only once the day has passed.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today.getTime();
}

const PROSPECT_STAGE_POINTS: Record<string, number> = {
  new: 15,
  qualified: 35,
  proposal: 55,
  'closed-won': 100,
  'closed-lost': 0,
};

const CUSTOMER_STATUS_POINTS: Record<string, number> = {
  active: 70,
  paused: 40,
  churned: 5,
};

function bandFor(score: number): LeadBand {
  if (score >= 70) return 'hot';
  if (score >= 40) return 'warm';
  return 'cold';
}

export function scoreLead(person: Person): LeadScore {
  const factors: ScoreFactor[] = [];
  let score = 0;

  // Dead-end states pin cold — the breakdown still explains why.
  const deadEnd =
    person.stage === 'closed-lost' || person.status === 'churned';

  if (person.category === 'prospect') {
    const stage = person.stage ?? 'new';
    const sp = PROSPECT_STAGE_POINTS[stage] ?? 15;
    score += sp;
    factors.push({ label: `Stage: ${stage}`, points: sp });

    const vp = valuePoints(person.estimatedValue);
    if (vp) {
      score += vp;
      factors.push({ label: 'Deal value', points: vp });
    }
  } else if (person.category === 'customer') {
    const status = person.status ?? 'active';
    const sp = CUSTOMER_STATUS_POINTS[status] ?? 40;
    score += sp;
    factors.push({ label: `Status: ${status}`, points: sp });

    const vp = valuePoints(person.accountValue);
    if (vp) {
      score += vp;
      factors.push({ label: 'Account value', points: vp });
    }
  }

  if (!deadEnd) {
    const rec = recencyFactor(person);
    if (rec) {
      score += rec.points;
      factors.push(rec);
    }
    if (isFollowUpOverdue(person)) {
      score -= 10;
      factors.push({ label: 'Follow-up overdue', points: -10 });
    }
  }

  if (deadEnd) {
    score = Math.min(score, 10);
  }
  score = Math.max(0, Math.min(100, Math.round(score)));

  return { score, band: bandFor(score), factors };
}

/**
 * Fully-explained lead score. Reuses `scoreLead`'s exact rules (the
 * numeric score, band, and every underlying factor are identical), but
 * guarantees the factors sum precisely to the score by exposing the
 * otherwise-silent adjustments — the dead-end "pin cold" cap and the
 * 0–100 clamp — as labeled factors of their own. Mirrors how
 * `health.ts` itemizes CustomerHealth: no hidden math, every point
 * accounted for, so the UI can honestly answer "why this number?".
 */
export function scoreLeadWithFactors(person: Person): LeadScore {
  const base = scoreLead(person);
  const factors = [...base.factors];
  const raw = factors.reduce((n, f) => n + f.points, 0);
  const delta = base.score - raw;

  if (delta !== 0) {
    const deadEnd =
      person.stage === 'closed-lost' || person.status === 'churned';
    let label: string;
    if (deadEnd && delta < 0) {
      label = `Pinned cold — ${person.stage === 'closed-lost' ? 'closed-lost' : 'churned'}`;
    } else if (delta < 0) {
      label = 'Capped at 100';
    } else {
      label = 'Floored at 0';
    }
    factors.push({ label, points: delta });
  }

  return { score: base.score, band: base.band, factors };
}
